import React, { useDeferredValue, useEffect, useMemo, useState } from "react";
import Papa from "papaparse";

import { TIKTOK_ADMIN_API_BASE as API_BASE, apiHeaders } from "../config/api";
const adminFetch = (url, options = {}) => fetch(url, { ...options, headers: apiHeaders(options.headers) });
const METADATA_URL =
  "https://mindgamedataapi.s3.us-east-1.amazonaws.com/metadata.csv";
const TRACKING_FREQUENCIES = [1, 3, 6, 12, 24];

export default function TikTokAdmin() {
  const [data, setData] = useState([]);
  const [latestMap, setLatestMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const [healthReport, setHealthReport] = useState(null);
  const [healthError, setHealthError] = useState("");

  const [metadataCompare, setMetadataCompare] = useState(null);
  const [metadataCompareError, setMetadataCompareError] = useState("");

  const [bulkText, setBulkText] = useState("");
  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [frequencyFilter, setFrequencyFilter] = useState("all");

  const [sortKey, setSortKey] = useState("game");
  const [sortDirection, setSortDirection] = useState("asc");

  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({ game: "", hashtag: "" });

  const deferredBulkText = useDeferredValue(bulkText);

  const fetchConfig = async () => {
    const res = await adminFetch(`${API_BASE}/config`);
    const json = await res.json();
    setData(json);
  };

  const fetchLatest = async () => {
    const res = await adminFetch(`${API_BASE}/latest`);
    const json = await res.json();

    const map = {};
    json.forEach((row) => {
      map[row.config_id] = row;
    });
    setLatestMap(map);
  };

  const refreshAll = async () => {
    setLoading(true);
    try {
      await Promise.all([fetchConfig(), fetchLatest()]);
    } finally {
      setLoading(false);
    }
  };

  const handleHealthCheck = async () => {
    setBusyId("health-check");
    setHealthError("");

    try {
      const res = await adminFetch(`${API_BASE}/health`);
      if (!res.ok)
        throw new Error(`Health check failed with HTTP ${res.status}`);

      const json = await res.json();
      setHealthReport(json);
    } catch (err) {
      setHealthError(String(err.message || err));
    } finally {
      setBusyId(null);
    }
  };

  useEffect(() => {
    refreshAll();
  }, []);

  const normalizeGameName = (value) =>
    String(value || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, " ");

  const handleMetadataCompare = async () => {
    setBusyId("metadata-compare");
    setMetadataCompareError("");

    try {
      const res = await fetch(METADATA_URL);
      if (!res.ok)
        throw new Error(`Metadata fetch failed with HTTP ${res.status}`);

      const csvText = await res.text();

      const parsed = Papa.parse(csvText, {
        header: true,
        skipEmptyLines: true,
      });

      const metadataRows = parsed.data.filter((row) =>
        String(row.Game || "").trim(),
      );

      const trackedSet = new Set(
        data.map((row) => normalizeGameName(row.game)).filter(Boolean),
      );

      const missing = metadataRows
        .filter((row) => !trackedSet.has(normalizeGameName(row.Game)))
        .map((row) => ({
          game: row.Game || "",
          release_date: row.Release_Date || "",
          publisher: row.Publisher || "",
          genre: row.Genre || "",
          tags: row.Tags || "",
        }))
        .sort((a, b) => String(a.game).localeCompare(String(b.game)));

      setMetadataCompare({
        metadata_total: metadataRows.length,
        tiktok_total: data.length,
        matched_count: metadataRows.length - missing.length,
        missing_count: missing.length,
        missing,
      });
    } catch (err) {
      setMetadataCompareError(String(err.message || err));
    } finally {
      setBusyId(null);
    }
  };

  const parseBulkRows = (text) => {
    return text
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean)
      .flatMap((line) => {
        const parts = line.split(",");
        const game = (parts[0] || "").trim();
        const hashtagText = parts.slice(1).join(",").trim();

        return hashtagText
          .split(/[;|]/)
          .map((tag) => ({
            game,
            hashtag: tag.trim(),
          }));
      })
      .map((row) => ({
        game: row.game,
        hashtag: row.hashtag.replace(/^#/, ""),
      }))
      .filter((row) => row.game && row.hashtag)
      .filter((row) => {
        const gameLower = row.game.toLowerCase();
        const hashtagLower = row.hashtag.toLowerCase().replace(/^#/, "");
        return !(gameLower === "game" && hashtagLower === "hashtag");
      });
  };

  const handleBulkAdd = async () => {
    const rows = parseBulkRows(bulkText);
    if (!rows.length) {
      alert(
        "No valid rows found. Use format: Game,Hashtag and do not include a header row.",
      );
      return;
    }

    setBusyId("bulk");
    try {
      const res = await adminFetch(`${API_BASE}/config/bulk`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          rows,
          tracking_frequency_hours: 24,
          priority_tier: "daily",
          is_active: true,
          last_edited_by: "admin",
        }),
      });

      const json = await res.json();
      alert(
        `Add complete.\nInserted: ${json.inserted_count}\nSkipped: ${json.skipped_count}`,
      );
      setBulkText("");
      await refreshAll();
    } finally {
      setBusyId(null);
    }
  };

  const handleCsvUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const text = await file.text();
    const rows = parseBulkRows(text);

    if (!rows.length) {
      alert(
        "CSV upload found no valid rows. Expected format: Game,Hashtag with NO header row.",
      );
      return;
    }

    setBusyId("csv");
    try {
      const res = await adminFetch(`${API_BASE}/config/bulk`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          rows,
          tracking_frequency_hours: 24,
          priority_tier: "daily",
          is_active: true,
          last_edited_by: "admin",
        }),
      });

      const json = await res.json();
      alert(
        `CSV upload complete.\nInserted: ${json.inserted_count}\nSkipped: ${json.skipped_count}`,
      );
      event.target.value = "";
      await refreshAll();
    } finally {
      setBusyId(null);
    }
  };

  const handleToggleActive = async (row) => {
    setBusyId(row.id);
    try {
      await adminFetch(`${API_BASE}/config/${row.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          is_active: !row.is_active,
          last_edited_by: "admin",
        }),
      });

      await refreshAll();
    } finally {
      setBusyId(null);
    }
  };

  const handleFrequencyChange = async (row, hours) => {
    setBusyId(row.id);
    try {
      await adminFetch(`${API_BASE}/config/${row.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tracking_frequency_hours: hours,
          last_edited_by: "admin",
        }),
      });

      await refreshAll();
    } finally {
      setBusyId(null);
    }
  };

  const handleStartEdit = (row) => {
    setEditingId(row.id);
    setEditDraft({
      game: row.game || "",
      hashtag: row.hashtag || "",
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditDraft({ game: "", hashtag: "" });
  };

  const handleSaveEdit = async (row) => {
    const game = editDraft.game.trim();
    const hashtag = editDraft.hashtag.trim().replace(/^#/, "");

    if (!game || !hashtag) {
      alert("Game and hashtag are required.");
      return;
    }

    const confirmed = window.confirm(
      `Update this row?\n\nOld: ${row.game} / #${row.hashtag}\nNew: ${game} / #${hashtag}\n\nThis keeps the same config_id and existing snapshot history.`,
    );
    if (!confirmed) return;

    setBusyId(`edit-${row.id}`);
    try {
      await adminFetch(`${API_BASE}/config/${row.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          game,
          hashtag,
          last_edited_by: "admin",
        }),
      });

      setEditingId(null);
      setEditDraft({ game: "", hashtag: "" });
      await refreshAll();
    } finally {
      setBusyId(null);
    }
  };

  const handleRunNowAll = async () => {
    const confirmed = window.confirm(
      "Mark ALL active TikTok rows as due immediately? This does not scrape instantly by itself, but the next scraper run will pick them all up.",
    );
    if (!confirmed) return;

    setBusyId("run-all");
    try {
      const res = await adminFetch(`${API_BASE}/run-now`, {
        method: "POST",
      });
      const json = await res.json();
      alert(json.message);
      await refreshAll();
    } finally {
      setBusyId(null);
    }
  };

  const handleRunOneNow = async (row) => {
    setBusyId(`run-${row.id}`);
    try {
      const res = await adminFetch(`${API_BASE}/run-one-now/${row.id}`, {
        method: "POST",
      });
      const json = await res.json();
      alert(json.message);
      await refreshAll();
    } finally {
      setBusyId(null);
    }
  };

  const handleRunOneInstant = async (row) => {
    setBusyId(`instant-${row.id}`);
    try {
      const res = await adminFetch(`${API_BASE}/run-one-instant/${row.id}`, {
        method: "POST",
      });
      const json = await res.json();

      const result = json?.result || {};
      alert(
        `${json.message}\n\nVideos: ${result.videos ?? "-"}\nViews: ${result.views ?? "-"}\nStatus: ${result.status ?? "-"}`,
      );

      await refreshAll();
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (row) => {
    const confirmed = window.confirm(
      `Delete "${row.game}" and ALL of its stored TikTok data? This cannot be undone.`,
    );
    if (!confirmed) return;

    setBusyId(`delete-${row.id}`);
    try {
      const res = await adminFetch(`${API_BASE}/config/${row.id}`, {
        method: "DELETE",
      });
      const json = await res.json();
      alert(json.message);
      await refreshAll();
    } finally {
      setBusyId(null);
    }
  };

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDirection("asc");
    }
  };

  const filteredData = useMemo(() => {
    const q = search.trim().toLowerCase().replace(/^#/, "");

    const filtered = data.filter((row) => {
      const idText = String(row.id || "").toLowerCase();
      const gameText = String(row.game || "").toLowerCase();
      const gameKeyText = String(row.game_key || "").toLowerCase();
      const hashtagText = String(row.hashtag || "").toLowerCase();
      const hashtagWithHash = `#${hashtagText}`;
      const lastErrorText = String(row.last_error_message || "").toLowerCase();
      const matchesActive =
        activeFilter === "all" ||
        (Number(row.is_active) === 1) === (activeFilter === "active");
      const matchesFrequency =
        frequencyFilter === "all" ||
        Number(row.tracking_frequency_hours) === Number(frequencyFilter);

      return (
        matchesActive &&
        matchesFrequency &&
        (idText.includes(q) ||
          gameText.includes(q) ||
          gameKeyText.includes(q) ||
          hashtagText.includes(q) ||
          hashtagWithHash.includes(search.trim().toLowerCase()) ||
          lastErrorText.includes(q))
      );
    });

    const sorted = [...filtered].sort((a, b) => {
      const latestA = latestMap[a.id] || {};
      const latestB = latestMap[b.id] || {};

      let valA;
      let valB;

      switch (sortKey) {
        case "id":
          valA = Number(a.id || 0);
          valB = Number(b.id || 0);
          break;
        case "game":
          valA = String(a.game_key || a.game || "").toLowerCase();
          valB = String(b.game_key || b.game || "").toLowerCase();
          break;
        case "hashtag":
          valA = String(a.hashtag || "").toLowerCase();
          valB = String(b.hashtag || "").toLowerCase();
          break;
        case "is_active":
          valA = Number(a.is_active || 0);
          valB = Number(b.is_active || 0);
          break;
        case "tracking_frequency_hours":
          valA = Number(a.tracking_frequency_hours || 0);
          valB = Number(b.tracking_frequency_hours || 0);
          break;
        case "videos":
          valA = Number(latestA.videos || 0);
          valB = Number(latestB.videos || 0);
          break;
        case "views":
          valA = Number(latestA.views || 0);
          valB = Number(latestB.views || 0);
          break;
        case "last_run_at":
          valA = a.last_run_at ? new Date(a.last_run_at).getTime() : 0;
          valB = b.last_run_at ? new Date(b.last_run_at).getTime() : 0;
          break;
        case "next_run_at":
          valA = a.next_run_at ? new Date(a.next_run_at).getTime() : 0;
          valB = b.next_run_at ? new Date(b.next_run_at).getTime() : 0;
          break;
        case "consecutive_error_count":
          valA = Number(a.consecutive_error_count || 0);
          valB = Number(b.consecutive_error_count || 0);
          break;
        case "last_error_at":
          valA = a.last_error_at ? new Date(a.last_error_at).getTime() : 0;
          valB = b.last_error_at ? new Date(b.last_error_at).getTime() : 0;
          break;
        default:
          valA = String(a.game || "").toLowerCase();
          valB = String(b.game || "").toLowerCase();
      }

      if (valA < valB) return sortDirection === "asc" ? -1 : 1;
      if (valA > valB) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [data, search, activeFilter, frequencyFilter, sortKey, sortDirection, latestMap]);

  const handleExportCsv = () => {
    const rows = filteredData.map((row) => {
      const latest = latestMap[row.id] || {};
      return {
        config_id: row.id,
        game_key: row.game_key || row.game,
        game: row.game,
        hashtag: row.hashtag,
        is_active: row.is_active,
        tracking_frequency_hours: row.tracking_frequency_hours,
        videos: latest.videos ?? "",
        views: latest.views ?? "",
        last_run_at: row.last_run_at ?? "",
        next_run_at: row.next_run_at ?? "",
        consecutive_error_count: row.consecutive_error_count ?? 0,
        last_error_at: row.last_error_at ?? "",
        last_error_status: row.last_error_status ?? "",
        last_error_message: row.last_error_message ?? "",
        priority_tier: row.priority_tier ?? "",
        notes: row.notes ?? "",
        last_edited_by: row.last_edited_by ?? "",
      };
    });

    const headers = Object.keys(
      rows[0] || {
        config_id: "",
        game_key: "",
        game: "",
        hashtag: "",
        is_active: "",
        tracking_frequency_hours: "",
        videos: "",
        views: "",
        last_run_at: "",
        next_run_at: "",
        consecutive_error_count: "",
        last_error_at: "",
        last_error_status: "",
        last_error_message: "",
        priority_tier: "",
        notes: "",
        last_edited_by: "",
      },
    );

    const escapeCsv = (value) => {
      const str = String(value ?? "");
      if (str.includes(",") || str.includes('"') || str.includes("\n")) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const csvLines = [
      headers.join(","),
      ...rows.map((row) => headers.map((h) => escapeCsv(row[h])).join(",")),
    ];

    const blob = new Blob([csvLines.join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = "tiktok_tracking_export.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleExportMissingTikTokCsv = () => {
    if (!metadataCompare?.missing?.length) {
      alert("No missing TikTok rows to export.");
      return;
    }

    const headers = ["game", "release_date", "publisher", "genre", "tags"];
    const escapeCsv = (value) => {
      const str = String(value ?? "");
      if (str.includes(",") || str.includes('"') || str.includes("\n")) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const csvLines = [
      headers.join(","),
      ...metadataCompare.missing.map((row) =>
        headers.map((h) => escapeCsv(row[h])).join(","),
      ),
    ];

    const blob = new Blob([csvLines.join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = "missing_tiktok_tracking_rows.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const stats = useMemo(() => {
    const now = Date.now();

    const activeCount = data.filter(
      (row) => Number(row.is_active) === 1,
    ).length;
    const pausedCount = data.filter((row) => !Number(row.is_active)).length;
    const errorCount = data.filter(
      (row) => Number(row.consecutive_error_count || 0) > 0,
    ).length;
    const dueNowCount = data.filter((row) => {
      if (!row.next_run_at || !Number(row.is_active)) return false;
      return new Date(row.next_run_at).getTime() <= now;
    }).length;
    const rowsWithLatest = Object.keys(latestMap).length;
    const gameGroupMap = new Map();

    data.forEach((row) => {
      const key = row.game_key || row.game;
      if (!key) return;
      const set = gameGroupMap.get(key) || new Set();
      set.add(row.hashtag);
      gameGroupMap.set(key, set);
    });

    const multiHashtagGroups = Array.from(gameGroupMap.values()).filter(
      (hashtags) => hashtags.size > 1,
    ).length;

    return {
      total: data.length,
      activeCount,
      pausedCount,
      errorCount,
      dueNowCount,
      rowsWithLatest,
      gameGroupCount: gameGroupMap.size,
      multiHashtagGroups,
    };
  }, [data, latestMap]);

  const sortIndicator = (key) => {
    if (sortKey !== key) return " ↕";
    return sortDirection === "asc" ? " ↑" : " ↓";
  };

  const parseUtcDate = (value) => {
    if (!value) return null;
    if (value instanceof Date) return value;

    const str = String(value).trim();
    if (!str) return null;

    const normalized =
      /z$/i.test(str) || /[+-]\d{2}:\d{2}$/.test(str)
        ? str
        : str.replace(" ", "T") + "Z";

    const d = new Date(normalized);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const formatDateTimePST = (value) => {
    const d = parseUtcDate(value);
    if (!d) return value || "-";

    return d.toLocaleString("en-US", {
      timeZone: "America/Los_Angeles",
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const hashtagUrl = (hashtag) =>
    `https://www.tiktok.com/tag/${String(hashtag || "")
      .trim()
      .replace(/^#/, "")}`;

  const sectionCardStyle = {
    background: "#181818",
    border: "1px solid #2a2a2a",
    borderRadius: 10,
    padding: 16,
    marginBottom: 18,
  };

  const sectionTitleStyle = {
    margin: 0,
    fontSize: 18,
    fontWeight: 700,
  };

  const headerMetaTextStyle = {
    fontSize: 12,
    color: "#a9a9a9",
    lineHeight: 1.35,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  };

  const smallActionLinkStyle = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "4px 8px",
    background: "#242424",
    color: "#fff",
    textDecoration: "none",
    borderRadius: 4,
    fontSize: 12,
    fontWeight: 600,
    fontFamily: "inherit",
    border: "1px solid #3a3a3a",
    whiteSpace: "nowrap",
    lineHeight: 1.2,
  };

  const statCardStyle = {
    background: "#101010",
    border: "1px solid #2f2f2f",
    borderRadius: 8,
    padding: "12px 14px",
    minWidth: 160,
    flex: "1 1 160px",
  };

  const coloredButtonBaseStyle = {
    padding: "4px 8px",
    borderRadius: 4,
    fontSize: 12,
    fontWeight: 600,
    fontFamily: "inherit",
    border: "1px solid transparent",
    color: "#fff",
    cursor: "pointer",
    lineHeight: 1.2,
  };

  const greenButtonStyle = {
    ...coloredButtonBaseStyle,
    background: "#1f5f3b",
    borderColor: "#2f8a57",
  };

  const redButtonStyle = {
    ...coloredButtonBaseStyle,
    background: "#6a2c2c",
    borderColor: "#b35a5a",
  };

  const standardButtonStyle = {
    padding: "8px 14px",
    background: "#3b3b3b",
    color: "#fff",
    border: "1px solid #5a5a5a",
    borderRadius: 4,
    fontSize: 12,
    fontWeight: 600,
    fontFamily: "inherit",
    lineHeight: 1.2,
    cursor: "pointer",
  };

  const combinedRowsPreview = useMemo(
    () => parseBulkRows(deferredBulkText),
    [deferredBulkText],
  );
  const singlePreviewRow =
    combinedRowsPreview.length === 1 ? combinedRowsPreview[0] : null;

  const latestHealthRun = healthReport?.recent_runs?.[0] || null;
  const latestHealthDay = healthReport?.daily_runs?.[0] || null;

  const dbSizeMb = Number(healthReport?.database_size?.total_db_mb || 0);
  const totalSnapshots = Number(
    healthReport?.snapshot_volume?.total_snapshots || 0,
  );
  const snapshots30d = Number(
    healthReport?.snapshot_volume?.snapshots_30d || 0,
  );

  const dbSizeColor =
    dbSizeMb >= 20000 ? "#ff7b7b" : dbSizeMb >= 10000 ? "#ffcf66" : "#7CFFB2";

  const snapshotColor = totalSnapshots >= 10000000 ? "#ffcf66" : "#7CFFB2";

  const healthVerdictColor =
    healthReport?.verdict === "HEALTHY"
      ? "#7CFFB2"
      : healthReport?.verdict === "CAUTION"
        ? "#ffcf66"
        : healthReport?.verdict === "URGENT"
          ? "#ff7b7b"
          : "#aaa";

  const groupStats = useMemo(() => {
    const map = new Map();

    data.forEach((row) => {
      const key = row.game_key || row.game || "Unknown Game";
      const existing = map.get(key) || {
        rowCount: 0,
        hashtags: new Set(),
      };
      existing.rowCount += 1;
      if (row.hashtag) existing.hashtags.add(row.hashtag);
      map.set(key, existing);
    });

    return map;
  }, [data]);

  const tableRows = useMemo(() => {
    return filteredData.map((row) => {
      const latest = latestMap[row.id];
      const errorCount = Number(row.consecutive_error_count || 0);
      const hasError = errorCount > 0;
      const gameKey = row.game_key || row.game || "Unknown Game";
      const group = groupStats.get(gameKey);
      const isMultiHashtagGroup = Number(group?.hashtags?.size || 0) > 1;

      return (
        <tr
          key={row.id}
          style={{
            borderBottom: "1px solid #222",
            background: hasError ? "#1f1313" : "transparent",
          }}
        >
          <td style={{ padding: "6px", minWidth: 76, color: "#b8c7d9" }}>
            #{row.id}
          </td>

          <td style={{ padding: "6px", minWidth: 260 }}>
            {editingId === row.id ? (
              <input
                value={editDraft.game}
                onChange={(e) =>
                  setEditDraft((prev) => ({ ...prev, game: e.target.value }))
                }
                style={{
                  width: "100%",
                  padding: 6,
                  background: "#101010",
                  color: "#fff",
                  border: "1px solid #555",
                  borderRadius: 4,
                  fontFamily: "inherit",
                  fontSize: 13,
                }}
              />
            ) : (
              <div style={{ maxWidth: 220 }}>
                <div
                  title={row.game}
                  style={{
                    fontWeight: isMultiHashtagGroup ? 800 : 600,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {row.game}
                </div>
                {isMultiHashtagGroup && (
                  <div
                    style={{
                      marginTop: 3,
                      color: "#7ecbff",
                      fontSize: 11,
                      fontWeight: 700,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    Multi-tag group: {group.hashtags.size} hashtags
                  </div>
                )}
              </div>
            )}
          </td>

          <td style={{ padding: "6px", minWidth: 220 }}>
            {editingId === row.id ? (
              <input
                value={editDraft.hashtag}
                onChange={(e) =>
                  setEditDraft((prev) => ({ ...prev, hashtag: e.target.value }))
                }
                style={{
                  width: "100%",
                  padding: 6,
                  background: "#101010",
                  color: "#fff",
                  border: "1px solid #555",
                  borderRadius: 4,
                  fontFamily: "inherit",
                  fontSize: 13,
                }}
              />
            ) : (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  flexWrap: "nowrap",
                  whiteSpace: "nowrap",
                }}
              >
                <a
                  href={hashtagUrl(row.hashtag)}
                  target="_blank"
                  rel="noreferrer"
                  title={row.hashtag}
                  style={{
                    color: "#7ecbff",
                    maxWidth: 140,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    flexShrink: 1,
                    minWidth: 0,
                    textDecoration: "underline",
                  }}
                >
                  #{row.hashtag}
                </a>
                {isMultiHashtagGroup && (
                  <span
                    style={{
                      color: "#7ecbff",
                      fontSize: 11,
                      fontWeight: 800,
                      flexShrink: 0,
                    }}
                  >
                    config #{row.id}
                  </span>
                )}
              </div>
            )}
          </td>

          <td style={{ padding: "6px" }}>
            <button
              onClick={() => handleToggleActive(row)}
              disabled={busyId === row.id}
              title={
                row.is_active
                  ? "Tracking is currently active. Click to pause future tracking while keeping existing data."
                  : "Tracking is currently paused. Click to resume future tracking."
              }
              style={row.is_active ? greenButtonStyle : redButtonStyle}
            >
              {row.is_active ? "ON" : "OFF"}
            </button>
          </td>

          <td style={{ padding: "6px" }}>
            <select
              value={row.tracking_frequency_hours}
              onChange={(e) =>
                handleFrequencyChange(row, parseInt(e.target.value, 10))
              }
              title="How often this row should be tracked after the first successful capture."
              style={{ fontFamily: "inherit", fontSize: 12 }}
            >
              {TRACKING_FREQUENCIES.map((hours) => (
                <option key={hours} value={hours}>
                  {hours}h
                </option>
              ))}
            </select>
          </td>

          <td style={{ padding: "6px" }}>
            {latest?.videos != null
              ? Number(latest.videos).toLocaleString()
              : "-"}
          </td>

          <td style={{ padding: "6px" }}>
            {latest?.views != null
              ? Number(latest.views).toLocaleString()
              : "-"}
          </td>

          <td style={{ padding: "6px" }}>
            <span
              style={{
                display: "inline-block",
                minWidth: 28,
                textAlign: "center",
                padding: "2px 8px",
                borderRadius: 999,
                background: hasError ? "#5a2020" : "#202020",
                color: hasError ? "#ffb3b3" : "#d0d0d0",
                fontWeight: 700,
              }}
            >
              {errorCount}
            </span>
          </td>

          <td style={{ padding: "6px", maxWidth: 260 }}>
            {row.last_error_at ? (
              <div>
                <div
                  style={{ fontSize: 12, color: "#ffb3b3", marginBottom: 3 }}
                >
                  {formatDateTimePST(row.last_error_at)}
                </div>
                <div style={{ fontSize: 12, color: "#d5a7a7" }}>
                  {row.last_error_status || "-"}
                </div>
                <div
                  style={{
                    fontSize: 11,
                    color: "#b8b8b8",
                    marginTop: 3,
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                  }}
                >
                  {row.last_error_message || "-"}
                </div>
              </div>
            ) : (
              "-"
            )}
          </td>

          <td style={{ padding: "6px" }}>
            {formatDateTimePST(row.last_run_at)}
          </td>
          <td style={{ padding: "6px" }}>
            {formatDateTimePST(row.next_run_at)}
          </td>

          <td
            style={{
              padding: "6px",
              display: "flex",
              gap: 8,
              flexWrap: "nowrap",
              whiteSpace: "nowrap",
            }}
          >
            {editingId === row.id ? (
              <>
                <button
                  onClick={() => handleSaveEdit(row)}
                  disabled={busyId === `edit-${row.id}`}
                  style={greenButtonStyle}
                >
                  {busyId === `edit-${row.id}` ? "Saving..." : "Save Edit"}
                </button>

                <button onClick={handleCancelEdit} style={standardButtonStyle}>
                  Cancel
                </button>
              </>
            ) : (
              <button
                onClick={() => handleStartEdit(row)}
                style={standardButtonStyle}
              >
                Edit
              </button>
            )}

            <button
              onClick={() => handleRunOneInstant(row)}
              disabled={busyId === `instant-${row.id}`}
              title="Scrape this game immediately right now, save the first datapoint, and then resume automatic scheduled tracking based on Frequency."
              style={greenButtonStyle}
            >
              {busyId === `instant-${row.id}`
                ? "Running Now..."
                : "Run Now"}
            </button>

            <button
              onClick={() => handleRunOneNow(row)}
              disabled={busyId === `run-${row.id}`}
              title="Force game to be run ASAP in the next scheduled scraper run."
              style={greenButtonStyle}
            >
              {busyId === `run-${row.id}` ? "Marking..." : "Mark Due Now"}
            </button>

            <button
              onClick={() => handleDelete(row)}
              disabled={busyId === `delete-${row.id}`}
              title="Completely remove game, hashtag, and stored data from database - can't be undone!"
              style={redButtonStyle}
            >
              {busyId === `delete-${row.id}`
                ? "Deleting..."
                : "Delete + Wipe Data"}
            </button>
          </td>
        </tr>
      );
    });
  }, [filteredData, latestMap, busyId, editingId, editDraft, groupStats]);

  return (
    <div
      style={{
        padding: 20,
        background: "#111",
        color: "#fff",
        minHeight: "calc(var(--mg-vh) * 100)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 14,
          marginBottom: 18,
          flexWrap: "nowrap",
        }}
      >
        <img
          src="/TikTok Logo.png"
          alt="TikTok"
          style={{
            height: 52,
            width: "auto",
            display: "block",
            objectFit: "contain",
            flexShrink: 0,
          }}
        />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              gap: 10,
              minWidth: 0,
              flexWrap: "nowrap",
            }}
          >
            <h1
              style={{
                margin: 0,
                fontSize: 28,
                fontWeight: 800,
                flexShrink: 0,
              }}
            >
              Admin Control Center
            </h1>
            <span style={{ color: "#4c4c4c", flexShrink: 0 }}>|</span>
            <div style={{ ...headerMetaTextStyle, minWidth: 0 }}>
              Central command area for managing TikTok hashtag tracking, forcing
              runs, reviewing scrape health, pausing rows, and maintaining the
              full tracking database and snapshot flow.
            </div>
          </div>
        </div>
      </div>

      {stats.errorCount > 0 && (
        <div
          style={{
            marginBottom: 18,
            padding: "12px 14px",
            borderRadius: 8,
            border: "1px solid #6b2b2b",
            background: "#2a1313",
            color: "#ffd6d6",
            fontSize: 14,
            fontWeight: 600,
          }}
        >
          {stats.errorCount} tracked row{stats.errorCount === 1 ? "" : "s"}{" "}
          currently have retry / error state. Check the Error Count, Last Error,
          and Next Run columns below.
        </div>
      )}

      <div style={sectionCardStyle}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 14,
            minWidth: 0,
            flexWrap: "wrap",
          }}
        >
          <h2 style={sectionTitleStyle}>Tracking Health</h2>
          <span style={{ color: "#4c4c4c", flexShrink: 0 }}>|</span>
          <div
            style={{ ...headerMetaTextStyle, minWidth: 0, flex: "0 0 auto" }}
          >
            Quick status of the TikTok tracking system based on the current
            config rows and latest loaded data.
          </div>

          <button
            onClick={handleHealthCheck}
            disabled={busyId === "health-check"}
            style={standardButtonStyle}
          >
            {busyId === "health-check"
              ? "Running Health Check..."
              : "Run Master Health Check"}
          </button>
          <button
            onClick={handleMetadataCompare}
            disabled={busyId === "metadata-compare"}
            style={standardButtonStyle}
          >
            {busyId === "metadata-compare"
              ? "Checking Metadata..."
              : "Check Missing TikTok Rows"}
          </button>
        </div>

        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <div style={statCardStyle}>
            <div style={{ fontSize: 12, color: "#9a9a9a", marginBottom: 6 }}>
              Total Rows
            </div>
            <div style={{ fontSize: 24, fontWeight: 800 }}>
              {stats.total.toLocaleString()}
            </div>
          </div>

          <div style={statCardStyle}>
            <div style={{ fontSize: 12, color: "#9a9a9a", marginBottom: 6 }}>
              Active
            </div>
            <div style={{ fontSize: 24, fontWeight: 800, color: "#7CFFB2" }}>
              {stats.activeCount.toLocaleString()}
            </div>
          </div>

          <div style={statCardStyle}>
            <div style={{ fontSize: 12, color: "#9a9a9a", marginBottom: 6 }}>
              Paused
            </div>
            <div style={{ fontSize: 24, fontWeight: 800, color: "#ffcf66" }}>
              {stats.pausedCount.toLocaleString()}
            </div>
          </div>

          <div style={statCardStyle}>
            <div style={{ fontSize: 12, color: "#9a9a9a", marginBottom: 6 }}>
              Rows in Error / Retry
            </div>
            <div
              style={{
                fontSize: 24,
                fontWeight: 800,
                color: stats.errorCount > 0 ? "#ff7b7b" : "#fff",
              }}
            >
              {stats.errorCount.toLocaleString()}
            </div>
          </div>

          <div style={statCardStyle}>
            <div style={{ fontSize: 12, color: "#9a9a9a", marginBottom: 6 }}>
              Due Right Now
            </div>
            <div style={{ fontSize: 24, fontWeight: 800 }}>
              {stats.dueNowCount.toLocaleString()}
            </div>
          </div>

          <div style={statCardStyle}>
            <div style={{ fontSize: 12, color: "#9a9a9a", marginBottom: 6 }}>
              Rows with Latest Snapshot
            </div>
            <div style={{ fontSize: 24, fontWeight: 800 }}>
              {stats.rowsWithLatest.toLocaleString()}
            </div>
          </div>

          <div style={statCardStyle}>
            <div style={{ fontSize: 12, color: "#9a9a9a", marginBottom: 6 }}>
              Multi-Hashtag Games
            </div>
            <div style={{ fontSize: 24, fontWeight: 800, color: "#7ecbff" }}>
              {stats.multiHashtagGroups.toLocaleString()}
            </div>
            <div style={{ fontSize: 12, color: "#aaa", marginTop: 4 }}>
              Game groups: {stats.gameGroupCount.toLocaleString()}
            </div>
          </div>
        </div>

        <div style={{ marginTop: 16 }}>
          {healthError && (
            <div
              style={{
                marginTop: 12,
                padding: 12,
                borderRadius: 8,
                background: "#2a1313",
                border: "1px solid #6b2b2b",
                color: "#ffd6d6",
                fontSize: 13,
              }}
            >
              Health check failed: {healthError}
            </div>
          )}

          {metadataCompareError && (
            <div
              style={{
                marginTop: 12,
                padding: 12,
                borderRadius: 8,
                background: "#2a1313",
                border: "1px solid #6b2b2b",
                color: "#ffd6d6",
                fontSize: 13,
              }}
            >
              Metadata comparison failed: {metadataCompareError}
            </div>
          )}

          {metadataCompare && (
            <div
              style={{
                marginTop: 14,
                padding: 14,
                borderRadius: 10,
                background: "#101010",
                border: "1px solid #303030",
              }}
            >
              <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 10 }}>
                mindGAME Metadata vs TikTok Tracking
              </div>

              <button
                onClick={handleExportMissingTikTokCsv}
                style={{ ...standardButtonStyle, marginBottom: 12 }}
              >
                Export Missing TikTok Rows to CSV
              </button>

              <div
                style={{
                  display: "flex",
                  gap: 12,
                  flexWrap: "wrap",
                  marginBottom: 14,
                }}
              >
                <div style={statCardStyle}>
                  <div
                    style={{ fontSize: 12, color: "#9a9a9a", marginBottom: 6 }}
                  >
                    Metadata Games
                  </div>
                  <div style={{ fontSize: 18, fontWeight: 800 }}>
                    {metadataCompare.metadata_total.toLocaleString()}
                  </div>
                </div>

                <div style={statCardStyle}>
                  <div
                    style={{ fontSize: 12, color: "#9a9a9a", marginBottom: 6 }}
                  >
                    TikTok Rows
                  </div>
                  <div style={{ fontSize: 18, fontWeight: 800 }}>
                    {metadataCompare.tiktok_total.toLocaleString()}
                  </div>
                </div>

                <div style={statCardStyle}>
                  <div
                    style={{ fontSize: 12, color: "#9a9a9a", marginBottom: 6 }}
                  >
                    Matched
                  </div>
                  <div
                    style={{ fontSize: 18, fontWeight: 800, color: "#7CFFB2" }}
                  >
                    {metadataCompare.matched_count.toLocaleString()}
                  </div>
                </div>

                <div style={statCardStyle}>
                  <div
                    style={{ fontSize: 12, color: "#9a9a9a", marginBottom: 6 }}
                  >
                    Missing TikTok Rows
                  </div>
                  <div
                    style={{
                      fontSize: 18,
                      fontWeight: 800,
                      color:
                        metadataCompare.missing_count > 0
                          ? "#ffcf66"
                          : "#7CFFB2",
                    }}
                  >
                    {metadataCompare.missing_count.toLocaleString()}
                  </div>
                </div>
              </div>

              {metadataCompare.missing.length > 0 && (
                <div
                  style={{
                    maxHeight: 320,
                    overflow: "auto",
                    border: "1px solid #242424",
                    borderRadius: 8,
                  }}
                >
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: 13,
                    }}
                  >
                    <thead>
                      <tr style={{ borderBottom: "1px solid #333" }}>
                        <th style={{ textAlign: "left", padding: "8px 6px" }}>
                          Game
                        </th>
                        <th style={{ textAlign: "left", padding: "8px 6px" }}>
                          Release
                        </th>
                        <th style={{ textAlign: "left", padding: "8px 6px" }}>
                          Publisher
                        </th>
                        <th style={{ textAlign: "left", padding: "8px 6px" }}>
                          Genre
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {metadataCompare.missing.slice(0, 250).map((row, idx) => (
                        <tr
                          key={`${row.game}-${idx}`}
                          style={{ borderBottom: "1px solid #222" }}
                        >
                          <td style={{ padding: "6px" }}>{row.game}</td>
                          <td style={{ padding: "6px" }}>
                            {row.release_date || "-"}
                          </td>
                          <td style={{ padding: "6px" }}>
                            {row.publisher || "-"}
                          </td>
                          <td style={{ padding: "6px" }}>{row.genre || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {metadataCompare.missing.length > 250 && (
                <div style={{ marginTop: 8, fontSize: 12, color: "#aaa" }}>
                  Showing first 250 missing rows.
                </div>
              )}
            </div>
          )}

          {healthReport && (
            <div
              style={{
                marginTop: 14,
                padding: 14,
                borderRadius: 10,
                background: "#101010",
                border: "1px solid #303030",
              }}
            >
              <div
                style={{
                  display: "flex",
                  gap: 12,
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <div
                  style={{
                    fontSize: 18,
                    fontWeight: 800,
                    color: healthVerdictColor,
                  }}
                >
                  {healthReport.verdict || "UNKNOWN"}
                </div>
                <div style={{ fontSize: 12, color: "#aaa" }}>
                  Checked:{" "}
                  {formatDateTimePST(
                    healthReport.checked_at_utc ||
                      healthReport.generated_at_utc ||
                      healthReport.checked_at,
                  )}
                </div>
              </div>

              <div
                style={{
                  marginTop: 12,
                  display: "flex",
                  gap: 12,
                  flexWrap: "wrap",
                }}
              >
                <div style={statCardStyle}>
                  <div
                    style={{ fontSize: 12, color: "#9a9a9a", marginBottom: 6 }}
                  >
                    Latest Run
                  </div>
                  <div style={{ fontSize: 18, fontWeight: 800 }}>
                    {latestHealthRun ? `#${latestHealthRun.id}` : "-"}
                  </div>
                  <div style={{ fontSize: 12, color: "#aaa", marginTop: 4 }}>
                    {latestHealthRun
                      ? `${latestHealthRun.rows_ok}/${latestHealthRun.rows_attempted} OK`
                      : "No recent run found"}
                  </div>
                </div>

                <div style={statCardStyle}>
                  <div
                    style={{ fontSize: 12, color: "#9a9a9a", marginBottom: 6 }}
                  >
                    Today OK Rate
                  </div>
                  <div style={{ fontSize: 18, fontWeight: 800 }}>
                    {latestHealthDay?.ok_rate_pct != null
                      ? `${latestHealthDay.ok_rate_pct}%`
                      : "-"}
                  </div>
                  <div style={{ fontSize: 12, color: "#aaa", marginTop: 4 }}>
                    Errors: {latestHealthDay?.error_rows ?? 0}
                  </div>
                </div>

                <div style={statCardStyle}>
                  <div
                    style={{ fontSize: 12, color: "#9a9a9a", marginBottom: 6 }}
                  >
                    Rows Due Now
                  </div>
                  <div style={{ fontSize: 18, fontWeight: 800 }}>
                    {(healthReport.due_now?.due_now ?? 0).toLocaleString()}
                  </div>
                </div>

                <div style={statCardStyle}>
                  <div
                    style={{ fontSize: 12, color: "#9a9a9a", marginBottom: 6 }}
                  >
                    Recent 0/0 Hashtags
                  </div>
                  <div style={{ fontSize: 18, fontWeight: 800 }}>
                    {(
                      healthReport.zero_zero_recent || []
                    ).length.toLocaleString()}
                  </div>
                </div>

                <div style={statCardStyle}>
                  <div
                    style={{ fontSize: 12, color: "#9a9a9a", marginBottom: 6 }}
                  >
                    Database Size
                  </div>
                  <div
                    style={{
                      fontSize: 18,
                      fontWeight: 800,
                      color: dbSizeColor,
                    }}
                  >
                    {dbSizeMb ? `${dbSizeMb.toLocaleString()} MB` : "-"}
                  </div>
                  <div style={{ fontSize: 12, color: "#aaa", marginTop: 4 }}>
                    {dbSizeMb >= 20000
                      ? "Large - archive soon"
                      : dbSizeMb >= 10000
                        ? "Growing - monitor"
                        : "Manageable"}
                  </div>
                </div>

                <div style={statCardStyle}>
                  <div
                    style={{ fontSize: 12, color: "#9a9a9a", marginBottom: 6 }}
                  >
                    Total Snapshots
                  </div>
                  <div
                    style={{
                      fontSize: 18,
                      fontWeight: 800,
                      color: snapshotColor,
                    }}
                  >
                    {totalSnapshots ? totalSnapshots.toLocaleString() : "-"}
                  </div>
                  <div style={{ fontSize: 12, color: "#aaa", marginTop: 4 }}>
                    30D: {snapshots30d ? snapshots30d.toLocaleString() : "-"}
                  </div>
                </div>
              </div>

              <div style={{ marginTop: 14 }}>
                {["urgent", "caution", "good"].map((group) => {
                  const items = healthReport?.[group] || [];
                  if (!items.length) return null;

                  const label =
                    group === "urgent"
                      ? "Urgent"
                      : group === "caution"
                        ? "Caution"
                        : "Looking Good";

                  const color =
                    group === "urgent"
                      ? "#ff7b7b"
                      : group === "caution"
                        ? "#ffcf66"
                        : "#7CFFB2";

                  return (
                    <div key={group} style={{ marginTop: 14 }}>
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 800,
                          marginBottom: 6,
                          color,
                        }}
                      >
                        {label}
                      </div>
                      <ul
                        style={{
                          margin: 0,
                          paddingLeft: 18,
                          color: "#d0d0d0",
                          fontSize: 13,
                          lineHeight: 1.5,
                        }}
                      >
                        {items.map((item, idx) => (
                          <li key={idx}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      <div style={sectionCardStyle}>
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 20,
            flexWrap: "wrap",
          }}
        >
          <div style={{ flex: "2 1 560px", minWidth: 0 }}>
            <div
              style={{
                display: "flex",
                alignItems: "baseline",
                gap: 10,
                marginBottom: 14,
                minWidth: 0,
                flexWrap: "wrap",
              }}
            >
              <h2 style={sectionTitleStyle}>Add New TikTok Games/Hashtags</h2>
              <div
                style={{
                  ...headerMetaTextStyle,
                  minWidth: 0,
                  whiteSpace: "normal",
                }}
              >
                Use Game,Hashtag. For multiple hashtags under the same game,
                separate hashtags with semicolons. Each hashtag becomes its own
                config row.
              </div>
            </div>

            <textarea
              value={bulkText}
              onChange={(e) => setBulkText(e.target.value)}
              placeholder={`Example:
2XKO,2xko
Marathon,marathongame
ARC Raiders,arcraiders
Grand Theft Auto 6,gta6;grandtheftauto6;grandtheftautovi`}
              style={{
                width: "100%",
                minHeight: 120,
                padding: 10,
                marginBottom: 12,
                background: "#101010",
                color: "#fff",
                border: "1px solid #333",
                borderRadius: 6,
                resize: "vertical",
                overflow: "hidden",
                fontFamily: "inherit",
                fontSize: 13,
                lineHeight: 1.45,
              }}
            />

            {singlePreviewRow && (
              <div
                style={{
                  marginBottom: 12,
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  flexWrap: "wrap",
                }}
              >
                <div style={{ fontSize: 12, color: "#a9a9a9" }}>
                  Preview hashtag link for{" "}
                  <strong>{singlePreviewRow.game}</strong>:
                </div>
                <a
                  href={hashtagUrl(singlePreviewRow.hashtag)}
                  target="_blank"
                  rel="noreferrer"
                  style={standardButtonStyle}
                >
                  Open TikTok Hashtag Page
                </a>
              </div>
            )}

            <div
              style={{
                display: "flex",
                gap: 12,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <button
                onClick={handleBulkAdd}
                disabled={busyId === "bulk"}
                style={standardButtonStyle}
              >
                {busyId === "bulk"
                  ? "Adding..."
                  : combinedRowsPreview.length <= 1
                    ? "Add 1 Row to Tracking Database"
                    : `Add ${combinedRowsPreview.length} Rows to Tracking Database`}
              </button>

              <label
                style={{
                  ...standardButtonStyle,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                Upload CSV File
                <input
                  type="file"
                  accept=".csv,text/csv"
                  onChange={handleCsvUpload}
                  style={{ display: "none" }}
                />
              </label>

              <div style={{ fontSize: 12, color: "#9a9a9a" }}>
                Valid rows detected:{" "}
                <strong>{combinedRowsPreview.length}</strong>
              </div>
            </div>
          </div>

          <div
            style={{
              flex: "1 1 280px",
              minWidth: 260,
              padding: 14,
              background: "#101010",
              border: "1px solid #2f2f2f",
              borderRadius: 8,
            }}
          >
            <h3 style={{ margin: "0 0 4px", fontSize: 15 }}>
              Suggested Tracking Frequency
            </h3>
            <div
              style={{
                marginBottom: 10,
                color: "#9a9a9a",
                fontSize: 12,
                lineHeight: 1.4,
              }}
            >
              Reference for manually setting a row’s frequency.
            </div>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: 13,
              }}
            >
              <thead>
                <tr style={{ borderBottom: "1px solid #3a3a3a" }}>
                  <th
                    scope="col"
                    style={{ padding: "7px 6px", textAlign: "left" }}
                  >
                    Lifetime views
                  </th>
                  <th
                    scope="col"
                    style={{ padding: "7px 6px", textAlign: "right" }}
                  >
                    Frequency
                  </th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["500M+", "1h"],
                  ["250M–500M", "3h"],
                  ["50M–250M", "6h"],
                  ["1M–50M", "12h"],
                  ["Under 1M", "24h"],
                ].map(([views, frequency]) => (
                  <tr key={views} style={{ borderBottom: "1px solid #262626" }}>
                    <td style={{ padding: "7px 6px" }}>{views}</td>
                    <td
                      style={{
                        padding: "7px 6px",
                        textAlign: "right",
                        color: "#7CFFB2",
                        fontWeight: 700,
                      }}
                    >
                      {frequency}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div style={sectionCardStyle}>
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            gap: 10,
            marginBottom: 14,
            minWidth: 0,
            flexWrap: "nowrap",
          }}
        >
          <h2 style={sectionTitleStyle}>
            Search, Sort, Pause, Run, or Delete Tracked Games
          </h2>
          <span style={{ color: "#4c4c4c", flexShrink: 0 }}>|</span>
          <div style={{ ...headerMetaTextStyle, minWidth: 0 }}>
            Search by game, hashtag, or error text. Sort by clicking column
            headers. Active ON/OFF pauses or resumes tracking. Use Run Now when
            you need the first datapoint immediately for a newly announced or
            time-sensitive game.
          </div>
        </div>

        <div
          style={{
            display: "flex",
            gap: 10,
            alignItems: "center",
            flexWrap: "wrap",
            marginBottom: 14,
          }}
        >
          <input
            placeholder="Search config ID, game, hashtag, or error..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              padding: 8,
              width: 320,
              fontFamily: "inherit",
              fontSize: 13,
            }}
          />

          <button
            onClick={handleRunNowAll}
            disabled={busyId === "run-all"}
            style={standardButtonStyle}
          >
            {busyId === "run-all"
              ? "Marking Due..."
              : "Mark All Active Rows Due Now"}
          </button>

          <button
            onClick={refreshAll}
            disabled={loading}
            style={standardButtonStyle}
          >
            {loading ? "Refreshing..." : "Refresh Admin Table"}
          </button>

          <button onClick={handleExportCsv} style={standardButtonStyle}>
            Export Current Admin List to CSV
          </button>
        </div>

        {loading ? (
          <div>Loading...</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: 14,
              }}
            >
              <thead>
                <tr style={{ borderBottom: "1px solid #333" }}>
                  <th
                    style={{
                      textAlign: "left",
                      padding: "8px 6px",
                      cursor: "pointer",
                    }}
                    onClick={() => handleSort("id")}
                  >
                    Config ID{sortIndicator("id")}
                  </th>
                  <th
                    style={{
                      textAlign: "left",
                      padding: "8px 6px",
                      cursor: "pointer",
                    }}
                    onClick={() => handleSort("game")}
                  >
                    Game{sortIndicator("game")}
                  </th>
                  <th
                    style={{
                      textAlign: "left",
                      padding: "8px 6px",
                      cursor: "pointer",
                    }}
                    onClick={() => handleSort("hashtag")}
                  >
                    Hashtag{sortIndicator("hashtag")}
                  </th>
                  <th
                    style={{
                      textAlign: "left",
                      padding: "8px 6px",
                      cursor: "pointer",
                    }}
                    onClick={() => handleSort("is_active")}
                  >
                    <div>Active{sortIndicator("is_active")}</div>
                    <select
                      aria-label="Filter by active status"
                      value={activeFilter}
                      onChange={(event) => setActiveFilter(event.target.value)}
                      onClick={(event) => event.stopPropagation()}
                      style={{
                        display: "block",
                        marginTop: 4,
                        fontFamily: "inherit",
                        fontSize: 12,
                      }}
                    >
                      <option value="all">All</option>
                      <option value="active">ON</option>
                      <option value="inactive">OFF</option>
                    </select>
                  </th>
                  <th
                    style={{
                      textAlign: "left",
                      padding: "8px 6px",
                      cursor: "pointer",
                    }}
                    onClick={() => handleSort("tracking_frequency_hours")}
                  >
                    <div>
                      Frequency (hrs){sortIndicator("tracking_frequency_hours")}
                    </div>
                    <select
                      aria-label="Filter by tracking frequency"
                      value={frequencyFilter}
                      onChange={(event) =>
                        setFrequencyFilter(event.target.value)
                      }
                      onClick={(event) => event.stopPropagation()}
                      style={{
                        display: "block",
                        marginTop: 4,
                        fontFamily: "inherit",
                        fontSize: 12,
                      }}
                    >
                      <option value="all">All</option>
                      {TRACKING_FREQUENCIES.map((hours) => (
                        <option key={hours} value={hours}>
                          {hours}h
                        </option>
                      ))}
                    </select>
                  </th>
                  <th
                    style={{
                      textAlign: "left",
                      padding: "8px 6px",
                      cursor: "pointer",
                    }}
                    onClick={() => handleSort("videos")}
                  >
                    Videos{sortIndicator("videos")}
                  </th>
                  <th
                    style={{
                      textAlign: "left",
                      padding: "8px 6px",
                      cursor: "pointer",
                    }}
                    onClick={() => handleSort("views")}
                  >
                    Views{sortIndicator("views")}
                  </th>
                  <th
                    style={{
                      textAlign: "left",
                      padding: "8px 6px",
                      cursor: "pointer",
                    }}
                    onClick={() => handleSort("consecutive_error_count")}
                  >
                    Error Count{sortIndicator("consecutive_error_count")}
                  </th>
                  <th
                    style={{
                      textAlign: "left",
                      padding: "8px 6px",
                      cursor: "pointer",
                    }}
                    onClick={() => handleSort("last_error_at")}
                  >
                    Last Error{sortIndicator("last_error_at")}
                  </th>
                  <th
                    style={{
                      textAlign: "left",
                      padding: "8px 6px",
                      cursor: "pointer",
                    }}
                    onClick={() => handleSort("last_run_at")}
                  >
                    Last Run (PST){sortIndicator("last_run_at")}
                  </th>
                  <th
                    style={{
                      textAlign: "left",
                      padding: "8px 6px",
                      cursor: "pointer",
                    }}
                    onClick={() => handleSort("next_run_at")}
                  >
                    Next Run (PST){sortIndicator("next_run_at")}
                  </th>
                  <th style={{ textAlign: "left", padding: "8px 6px" }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>{tableRows}</tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
